import type { Question, WebPageElement, WithContext } from "schema-dts"

import classNames from "classnames"
import { type FunctionComponent, useState } from "react"

import { Heading } from "@jobteaser/spark/components/Heading"
import IconCalendar from "@jobteaser/ui-kit/assets/icons/jds_calendar.svg"
import IconHouse from "@jobteaser/ui-kit/assets/icons/jds_house.svg"
import IconLanguage from "@jobteaser/ui-kit/assets/icons/jds_language.svg"
import IconLight from "@jobteaser/ui-kit/assets/icons/jds_light.svg"
import IconMedal from "@jobteaser/ui-kit/assets/icons/jds_medal.svg"
import IconStarOutline from "@jobteaser/ui-kit/assets/icons/jds_starOutline.svg"
import { Button, BUTTON_VARIANT } from "@jobteaser/ui-kit/components/Button"

import { QuestionCard } from "@/modules/companies/client/components/Faq/components/QuestionCard"
import { getQuestions } from "@/modules/companies/client/components/Faq/utils/getQuestions"
import { useCompanyTrack } from "@/modules/companies/client/hooks/tracking/useCompanyTrack"
import { useCompanyFetch } from "@/modules/companies/client/hooks/useCompanyFetch"
import { useTranslation } from "@/modules/i18n/components/useTranslation"

import styles from "./Faq.module.css"

type FAQProps = {
  className?: string
}
export const Faq: FunctionComponent<FAQProps> = ({ className }) => {
  const { t } = useTranslation(["companies_client"])
  const [displayAll, setDisplayAll] = useState(false)
  const { dataCompany } = useCompanyFetch()
  const companyTrack = useCompanyTrack()

  if (!dataCompany?.page.faq) {
    return null
  }

  const {
    id,
    page: { name },
  } = dataCompany

  const questions = getQuestions(dataCompany, t)
  const hasMoreQuestions = questions.length > 3
  const displayMoreQuestions = hasMoreQuestions && displayAll

  const icons = {
    customQuestion1: IconLight.id,
    customQuestion2: IconLanguage.id,
    hiringProfile: IconHouse.id,
    permanentContractOpportunity: IconMedal.id,
    remoteWorkPolicy: IconCalendar.id,
    youngTalentExperience: IconStarOutline.id,
  } as Record<string, string>

  const FAQSchema: WithContext<WebPageElement> = {
    "@context": "https://schema.org",
    "@type": "WebPageElement",
    mainEntity: questions.map(({ question, answer }): Question => {
      return {
        "@type": "Question",
        acceptedAnswer: {
          "@type": "Answer",
          text: answer,
        },
        name: question,
      }
    }),
  }

  return (
    <div className={classNames(styles.container, className)}>
      <Heading variant="title3" as="h3" weight="bold">
        {t("companies_client.information.faq.title")}
      </Heading>
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(FAQSchema),
        }}
      />
      <ul className={classNames(styles.questions, { [styles["questions--limited"]]: !displayMoreQuestions })}>
        {questions.map(question => (
          <QuestionCard key={question.id} icon={icons[question.id]} faqQuestion={question} />
        ))}
      </ul>
      {hasMoreQuestions && (
        <Button
          minor
          variant={BUTTON_VARIANT.GHOST}
          className={styles.button}
          onClick={() => {
            setDisplayAll(!displayAll)
            if (!displayAll) {
              companyTrack("see_more_information_click", {
                company_id: id,
                company_name: name,
                context: "company_faq",
              })
            }
          }}
          data-testid="company_information_faq_button"
        >
          {displayAll ? t("companies_client.information.faq.showLess") : t("companies_client.information.faq.showMore")}
        </Button>
      )}
    </div>
  )
}
